import { Flex, Table, Tag, Typography } from "antd";
import type { ColumnType } from "antd/lib/table";
import { ImpressaoContainer, ImpressaoHeader } from "components/Impressao";
import { format } from "date-fns";
import type { ApiColeta } from "pages/coleta/types/ApiColeta";
import type { ApiColetaItem } from "pages/coleta/types/ApiColetaItem";
import type { ApiProdutor } from "pages/produtores/types/ApiProdutor";
import { useMemo } from "react";
import { decimalFormater } from "std/format";
import { roundAbnt } from "std/math";

const { Title } = Typography;

export const columns: ColumnType<ApiColeta>[] = [
	{
		dataIndex: "clt_idpk",
		key: "clt_idpk",
		title: "Código",
	},
	{
		dataIndex: ["rota", "rot_identificacao"],
		key: "rot_identificacao",
		title: "Rota",
		render: (rot_identificacao, record) => (
			<>
				{rot_identificacao || ""}
				{record?.rota?.rot_obs ? ` (${record?.rota?.rot_obs})` : ""}
			</>
		),
	},
	{
		dataIndex: ["transportador", "tra_codigoindividual"],
		key: "transportador",
		title: "Transportador",
		render: (tra_codigoindividual, record) => (
			<>
				{tra_codigoindividual || ""} - {record?.transportador?.pessoa?.pes_nome_razaosocial || ""}
			</>
		),
	},
	{
		dataIndex: "clt_data",
		key: "clt_data",
		title: "Data",
		render: (value) => <>{value ? format(new Date(value), "dd/MM/yyyy") : ""}</>,
	},
	{
		dataIndex: "clt_volume",
		key: "clt_volume",
		title: "Volume Total",
		align: "right",
		render: (value) => decimalFormater(value || 0),
	},
];

export const columnsItens: ColumnType<ApiColetaItem>[] = [
	{
		dataIndex: "produtor",
		key: "for_codigoindividual",
		title: "Código",
		align: "center",
		render: (produtor: ApiProdutor) => <>{produtor?.for_codigoindividual || ""}</>,
	},
	{
		dataIndex: "produtor",
		key: "pes_nome_razaosocial",
		title: "Produtor",
		render: (produtor: ApiProdutor) => <>{produtor?.pessoa?.pes_nome_razaosocial || ""}</>,
	},
	{
		dataIndex: "clti_produto_nome",
		key: "clti_produto_nome",
		title: "Produto",
	},
	{
		dataIndex: "clti_descarte_litros",
		key: "clti_descarte_litros",
		title: "Litros Descarte",
		align: "right",
		render: (value) => decimalFormater(value || 0),
	},
	{
		dataIndex: "clti_descarte_motivo",
		key: "clti_descarte_motivo",
		title: "Motivo Descarte",
		align: "right",
		render: (value) => <>{value ? <Tag color="red">{value}</Tag> : ""}</>,
	},
	{
		dataIndex: "clti_volume",
		key: "clti_volume",
		title: "Volume",
		align: "right",
		render: (value) => decimalFormater(value || 0),
	},
];

export function TableRelLitragemColeta({ data, title }: { data: ApiColeta[] | undefined; title: string }) {
	const totalColetaVolume = useMemo(
		() =>
			roundAbnt(
				(data || []).reduce((acc, cur) => acc + Number(cur?.clt_volume || 0), 0),
				4,
			),
		[data],
	);

	// biome-ignore lint/correctness/noNestedComponentDefinitions: TODO
	const TableItens = (itens: ApiColetaItem[]): JSX.Element => {
		const totalVolume = roundAbnt(
			(itens || []).reduce((acc, cur) => acc + cur.clti_volume, 0),
			4,
		);

		return (
			<div style={{ paddingBottom: "18px", paddingTop: "4px" }}>
				<Table
					rowKey="clti_idpk"
					bordered={true}
					columns={columnsItens}
					dataSource={itens}
					pagination={false}
					summary={(rows) => (
						<Table.Summary.Row>
							<Table.Summary.Cell index={0} align="left" colSpan={2}>
								<div style={{ display: "flex" }}>
									<Flex vertical={true}>
										<Title level={5}>Registros: {rows?.length || 0}</Title>
									</Flex>
								</div>
							</Table.Summary.Cell>

							<Table.Summary.Cell index={1} align="right" colSpan={2}>
								<Flex vertical={true}>
									<Title level={5} style={{ margin: "0px" }}>
										Total: {decimalFormater(totalVolume)}
									</Title>
								</Flex>
							</Table.Summary.Cell>
						</Table.Summary.Row>
					)}
				/>
			</div>
		);
	};

	return (
		<ImpressaoContainer className="w-full mt-3" title={title}>
			<ImpressaoHeader title={title} />

			<Table
				rowKey="clt_idpk"
				columns={columns}
				dataSource={data}
				pagination={false}
				size="small"
				rowClassName="zebra"
				expandable={{
					expandedRowRender: (record) => TableItens(record?.itens || []),
					defaultExpandAllRows: true,
				}}
				summary={(rows) => (
					<Table.Summary.Row>
						<Table.Summary.Cell index={0} align="left" colSpan={3}>
							<div style={{ display: "flex" }}>
								<Flex vertical={true}>
									<Title level={4} style={{ margin: "0px" }}>
										<b>Qtd. Coletas: {rows?.length || 0}</b>
									</Title>
								</Flex>
							</div>
						</Table.Summary.Cell>

						<Table.Summary.Cell index={1} align="right" colSpan={3}>
							<Flex vertical={true}>
								<Title level={4} style={{ margin: "0px" }}>
									<b> Total Volume: {decimalFormater(totalColetaVolume)}</b>
								</Title>
							</Flex>
						</Table.Summary.Cell>
					</Table.Summary.Row>
				)}
			/>
		</ImpressaoContainer>
	);
}
